import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";

/**
 * Terms of Service page. Written in plain language, structured around the
 * exact PrismQuery product surface (pay-as-you-go coins, OpenRouter fan-out,
 * user-generated queries and pins). Not a substitute for a bespoke lawyer
 * review — replace the copy if a lawyer drafts you a custom document, but
 * keep the section anchors so future links keep working.
 */
export default function Terms() {
  const { t } = useLang();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="terms-page">
      <div className="glass-header sticky top-0 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 h-20">
          <button onClick={() => nav(-1)} className="btn-ghost focus-ring" data-testid="terms-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-[0.15em]">{t("profile.back")}</span>
          </button>
          <div className="label-eyebrow">{t("terms.eyebrow")}</div>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-14 space-y-10" data-testid="terms-content">
        <header>
          <div className="label-eyebrow mb-3">{t("terms.eyebrow")}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95] mb-3">
            {t("terms.title")}
          </h1>
          <p className="text-white/50 text-[13px] font-mono-p">{t("terms.updated")}</p>
        </header>

        <Section id="acceptance" title={t("terms.s1.title")}>
          <p>{t("terms.s1.body")}</p>
        </Section>

        <Section id="service" title={t("terms.s2.title")}>
          <p>{t("terms.s2.body")}</p>
        </Section>

        <Section id="account" title={t("terms.s3.title")}>
          <ul className="space-y-2">
            <li>{t("terms.s3.li1")}</li>
            <li>{t("terms.s3.li2")}</li>
            <li>{t("terms.s3.li3")}</li>
          </ul>
        </Section>

        <Section id="coins" title={t("terms.s4.title")}>
          <ul className="space-y-2">
            <li>{t("terms.s4.li1")}</li>
            <li>{t("terms.s4.li2")}</li>
            <li>{t("terms.s4.li3")}</li>
            <li>{t("terms.s4.li4")}</li>
          </ul>
        </Section>

        <Section id="acceptable-use" title={t("terms.s5.title")}>
          <p>{t("terms.s5.intro")}</p>
          <ul className="space-y-2 mt-3">
            <li>{t("terms.s5.li1")}</li>
            <li>{t("terms.s5.li2")}</li>
            <li>{t("terms.s5.li3")}</li>
            <li>{t("terms.s5.li4")}</li>
          </ul>
        </Section>

        <Section id="ai-content" title={t("terms.s6.title")}>
          <p>{t("terms.s6.body")}</p>
        </Section>

        <Section id="ip" title={t("terms.s7.title")}>
          <p>{t("terms.s7.body")}</p>
        </Section>

        <Section id="disclaimer" title={t("terms.s8.title")}>
          <p>{t("terms.s8.body")}</p>
        </Section>

        <Section id="liability" title={t("terms.s9.title")}>
          <p>{t("terms.s9.body")}</p>
        </Section>

        <Section id="termination" title={t("terms.s10.title")}>
          <p>{t("terms.s10.body")}</p>
        </Section>

        <Section id="changes" title={t("terms.s11.title")}>
          <p>{t("terms.s11.body")}</p>
        </Section>

        <Section id="law" title={t("terms.s12.title")}>
          <p>{t("terms.s12.body")}</p>
        </Section>

        <Section id="contact" title={t("terms.s13.title")}>
          <p>
            {t("terms.s13.body")}{" "}
            <Link to="/privacy" className="underline hover:text-white" data-testid="terms-privacy-link">
              {t("cookie.privacyLink")}
            </Link>
            .
          </p>
        </Section>
      </article>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id}>
      <h2 className="font-display text-xl font-black tracking-tight mb-3 text-white/95">
        {title}
      </h2>
      <div className="text-[14px] text-white/70 leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );
}
